import { EmptyRows } from "@/components/app/lists/empty-rows";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { WORK_ORDER_CATEGORY_LABELS } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Pencil, ShoppingCart } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useFeature } from "@/lib/policy";
import { dayOf, dollars, momentOf } from "../-lib/labels";
import { useMaintenanceBuilding } from "../-lib/use-maintenance-building";
import { PurchaseRequestForm } from "../-components/purchase-request-form";
import { ResearchPanel } from "../-components/research-panel";
import { UsePartForm } from "../-components/use-part-form";
import { PriorityBadge } from "../-components/priority-badge";
import { StatusBadge } from "../-components/status-badge";
import { WorkOrderControls } from "../-components/work-order-controls";
import { WorkOrderEditForm } from "../-components/work-order-edit-form";
import { WorkOrderLog } from "../-components/work-order-log";

export const Route = createFileRoute(
  "/_authenticated/_maintenance/maintenance/work-orders/$workOrderId",
)({ component: WorkOrderPage });

/** One work order in full: the facts, the controls to move it, the parts and the log. */
function WorkOrderPage() {
  const { workOrderId } = Route.useParams();
  const { facilityId, canManage } = useMaintenanceBuilding();
  const shop = useFeature("maintenance-shop", facilityId);
  const [asking, setAsking] = useState(false);
  const [editing, setEditing] = useState(false);
  const order = useQuery(
    orpc.agents.maintenance.workOrders.get.queryOptions({
      input: { facilityId, workOrderId },
    }),
  );

  if (order.isPending) return <Skeleton className="h-96 rounded-xl" />;
  if (order.isError) {
    return (
      <ReadFailed
        message="Couldn't load the work order."
        error={order.error}
        onRetry={() => void order.refetch()}
      />
    );
  }
  const row = order.data;
  if (row.facilityId !== facilityId) {
    return (
      <EmptyState
        title="This work order is at another building"
        description="Switch to its building to open it."
      />
    );
  }
  const facts: Array<[string, string]> = [
    ["Category", WORK_ORDER_CATEGORY_LABELS[row.category]],
    ["Where", row.location ?? "-"],
    ["Make and model", row.asset ?? "-"],
    ["Resident", row.residentName ?? "-"],
    ["Reported by", row.reporterName ?? "-"],
    ["Filed", momentOf(row.createdAt)],
    ["Due", dayOf(row.dueAt)],
    ["Started", dayOf(row.startedAt)],
    ["Completed", dayOf(row.completedAt)],
    ["Parts cost", dollars(row.partsCostCents)],
    ["Other cost", dollars(row.otherCostCents)],
    ["Total", dollars(row.totalCostCents)],
  ];

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/maintenance/work-orders">
            <ArrowLeft className="size-3.5" />
            Work orders
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground font-mono text-xs">{row.number}</p>
          <h2 className="text-lg font-semibold">{row.title}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusBadge status={row.status} />
            <PriorityBadge priority={row.priority} />
            {row.assigneeName ? (
              <span className="text-muted-foreground text-xs">With {row.assigneeName}</span>
            ) : null}
          </div>
        </div>
        {canManage ? (
          <Dialog open={editing} onOpenChange={setEditing}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <Pencil className="size-3.5" />
                Change
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Change {row.number}</DialogTitle>
                <DialogDescription>
                  What, where, how urgent, and what it has cost.
                </DialogDescription>
              </DialogHeader>
              <WorkOrderEditForm
                key={row.updatedAt.toString()}
                order={row}
                onSaved={() => setEditing(false)}
              />
            </DialogContent>
          </Dialog>
        ) : null}
        {canManage && shop ? (
          <Dialog open={asking} onOpenChange={setAsking}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <ShoppingCart className="size-3.5" />
                Ask to buy a part
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Ask to buy a part for {row.number}</DialogTitle>
                <DialogDescription>
                  It waits for someone who may approve the spend, then Nancy shops it.
                </DialogDescription>
              </DialogHeader>
              <PurchaseRequestForm
                facilityId={facilityId}
                workOrderId={row.id}
                onSaved={() => setAsking(false)}
              />
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>The fault</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {row.description ? (
                <p className="text-sm whitespace-pre-wrap">{row.description}</p>
              ) : (
                <EmptyRows text="No details were written." />
              )}
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                {facts.map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-muted-foreground text-xs">{label}</dt>
                    <dd className="truncate tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
              {row.resolution ? (
                <p className="text-sm">
                  <span className="text-muted-foreground">What was done: </span>
                  {row.resolution}
                </p>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Log</CardTitle>
            </CardHeader>
            <CardContent>
              <WorkOrderLog facilityId={facilityId} workOrderId={row.id} />
            </CardContent>
          </Card>
        </div>
        {canManage ? (
          <div className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Move it along</CardTitle>
              </CardHeader>
              <CardContent>
                <WorkOrderControls key={row.updatedAt.toString()} order={row} />
              </CardContent>
            </Card>
            {shop && row.open ? (
              <Card>
                <CardHeader>
                  <CardTitle>Parts</CardTitle>
                </CardHeader>
                <CardContent>
                  <UsePartForm facilityId={facilityId} workOrderId={row.id} />
                </CardContent>
              </Card>
            ) : null}
            <Card>
              <CardHeader>
                <CardTitle>David's research</CardTitle>
              </CardHeader>
              <CardContent>
                <ResearchPanel
                  facilityId={facilityId}
                  workOrderId={row.id}
                  asset={row.asset}
                  title={row.title}
                />
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </section>
  );
}
